import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";

const facePanelCss = `
.mgface-panel {
  width: 100%;
  color: #07111f;
}

.mgface-panel * {
  box-sizing: border-box;
}

.mgface-card {
  position: relative;
  overflow: hidden;
  margin: 14px 0;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid #cddcec;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.12), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  box-shadow: 0 14px 36px rgba(7, 17, 31, 0.065);
}

.mgface-card.dark {
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.22), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border-color: rgba(24, 183, 255, 0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgface-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.mgface-kicker {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgface-card.dark .mgface-kicker {
  color: #9fdcff;
}

.mgface-title {
  margin: 0;
  color: #07111f;
  font-size: 19px;
  line-height: 1.16;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgface-card.dark .mgface-title {
  color: #ffffff;
}

.mgface-subtitle {
  margin: 7px 0 0;
  color: #66778e;
  font-size: 13px;
  line-height: 1.55;
}

.mgface-card.dark .mgface-subtitle {
  color: #a9bdd3;
}

.mgface-chip {
  flex: 0 0 auto;
  min-height: 28px;
  padding: 7px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 950;
  white-space: nowrap;
}

.mgface-card.dark .mgface-chip {
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border-color: rgba(24,183,255,0.18);
}

.mgface-stage {
  position: relative;
  min-height: 330px;
  overflow: hidden;
  border-radius: 20px;
  background:
    radial-gradient(circle at 50% 46%, rgba(24,183,255,0.18), transparent 42%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.16);
}

.mgface-video,
.mgface-canvas-preview {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mgface-video {
  transform: scaleX(-1);
}

.mgface-stage-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 26px;
  color: white;
}

.mgface-orb {
  position: relative;
  width: 126px;
  height: 126px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 32% 24%, rgba(255,255,255,0.38), transparent 24%),
    linear-gradient(145deg, #18b7ff, #1f7bc1);
  box-shadow:
    0 0 0 12px rgba(24,183,255,0.08),
    0 24px 65px rgba(24,183,255,0.22);
  animation: mgfaceOrbPulse 2.4s ease-in-out infinite;
}

.mgface-orb::before,
.mgface-orb::after {
  content: "";
  position: absolute;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.38);
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.mgface-orb::before {
  width: 86px;
  height: 86px;
}

.mgface-orb::after {
  width: 52px;
  height: 52px;
}

.mgface-stage-placeholder strong {
  display: block;
  font-size: 17px;
  font-weight: 950;
}

.mgface-stage-placeholder span {
  display: block;
  max-width: 340px;
  margin-top: 8px;
  color: #a9bdd3;
  font-size: 13px;
  line-height: 1.55;
}

.mgface-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.mgface-scan-line {
  position: absolute;
  left: 8%;
  right: 8%;
  height: 2px;
  top: 18%;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, rgba(24,183,255,0.92), transparent);
  box-shadow: 0 0 24px rgba(24,183,255,0.62);
  animation: mgfaceScan 2.4s ease-in-out infinite;
}

.mgface-frame {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(220px, 62%);
  height: min(280px, 76%);
  transform: translate(-50%, -50%);
  border-radius: 48% 48% 44% 44%;
  border: 2px solid rgba(24,183,255,0.55);
  box-shadow:
    inset 0 0 28px rgba(24,183,255,0.10),
    0 0 28px rgba(24,183,255,0.16);
}

.mgface-frame::before,
.mgface-frame::after {
  content: "";
  position: absolute;
  top: 36%;
  width: 22%;
  height: 10%;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.65);
  background: rgba(24,183,255,0.12);
}

.mgface-frame::before {
  left: 20%;
}

.mgface-frame::after {
  right: 20%;
}

.mgface-corner {
  position: absolute;
  width: 38px;
  height: 38px;
  border-color: rgba(24,183,255,0.78);
  border-style: solid;
}

.mgface-corner.tl {
  left: 16px;
  top: 16px;
  border-width: 2px 0 0 2px;
  border-top-left-radius: 14px;
}

.mgface-corner.tr {
  right: 16px;
  top: 16px;
  border-width: 2px 2px 0 0;
  border-top-right-radius: 14px;
}

.mgface-corner.bl {
  left: 16px;
  bottom: 16px;
  border-width: 0 0 2px 2px;
  border-bottom-left-radius: 14px;
}

.mgface-corner.br {
  right: 16px;
  bottom: 16px;
  border-width: 0 2px 2px 0;
  border-bottom-right-radius: 14px;
}

.mgface-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 12px;
}

.mgface-btn {
  min-height: 38px;
  padding: 9px 13px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgface-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.mgface-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 12px 24px rgba(31, 123, 193, 0.20);
}

.mgface-btn.soft {
  color: #112238;
  background: #f8fbff;
  border-color: #cddcec;
}

.mgface-btn.danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
}

.mgface-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.mgface-metric {
  min-height: 82px;
  padding: 14px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.mgface-card.dark .mgface-metric {
  background: rgba(255,255,255,0.075);
  border-color: rgba(24,183,255,0.12);
}

.mgface-metric span {
  display: block;
  margin-bottom: 7px;
  color: #75869d;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.mgface-card.dark .mgface-metric span {
  color: #9fdcff;
}

.mgface-metric strong {
  display: block;
  color: #07111f;
  font-size: 16px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgface-card.dark .mgface-metric strong {
  color: #ffffff;
}

.mgface-metric small {
  display: block;
  margin-top: 5px;
  color: #66778e;
  line-height: 1.35;
}

.mgface-card.dark .mgface-metric small {
  color: #a9bdd3;
}

.mgface-status {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
  padding: 13px;
  border-radius: 16px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgface-card.dark .mgface-status {
  background: rgba(255,255,255,0.07);
  border-color: rgba(24,183,255,0.12);
}

.mgface-status strong {
  color: #07111f;
  font-size: 13px;
  font-weight: 950;
}

.mgface-card.dark .mgface-status strong {
  color: white;
}

.mgface-status span {
  display: block;
  margin-top: 4px;
  color: #66778e;
  font-size: 12px;
  line-height: 1.45;
}

.mgface-card.dark .mgface-status span {
  color: #a9bdd3;
}

.mgface-quality {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  align-content: center;
  background:
    radial-gradient(circle at center, #081525 58%, transparent 60%),
    conic-gradient(#18b7ff var(--quality-value), rgba(255,255,255,0.14) 0);
  border: 1px solid rgba(24,183,255,0.16);
}

.mgface-quality b {
  color: white;
  font-size: 18px;
  font-weight: 950;
  line-height: 1;
}

.mgface-quality small {
  color: #a9bdd3;
  font-size: 10px;
  font-weight: 850;
}

.mgface-section {
  margin-top: 14px;
  padding: 14px;
  border-radius: 17px;
  background: rgba(255,255,255,0.94);
  border: 1px solid #d9e8f6;
}

.mgface-card.dark .mgface-section {
  background: rgba(255,255,255,0.065);
  border-color: rgba(24,183,255,0.12);
}

.mgface-section h4 {
  margin: 0 0 8px;
  color: #07111f;
  font-size: 14px;
  font-weight: 950;
  letter-spacing: -0.02em;
}

.mgface-card.dark .mgface-section h4 {
  color: white;
}

.mgface-section p {
  margin: 0;
  color: #66778e;
  font-size: 13px;
  line-height: 1.6;
}

.mgface-card.dark .mgface-section p {
  color: #dbeafe;
}

.mgface-preview-grid {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  margin-top: 14px;
}

.mgface-preview-img {
  width: 112px;
  height: 140px;
  border-radius: 18px;
  object-fit: cover;
  border: 1px solid #d9e8f6;
  background: #f7fbff;
  box-shadow: 0 12px 28px rgba(7,17,31,0.08);
}

.mgface-card.dark .mgface-preview-img {
  border-color: rgba(24,183,255,0.16);
  background: rgba(255,255,255,0.06);
}

.mgface-list {
  display: grid;
  gap: 8px;
}

.mgface-list-item {
  display: grid;
  gap: 4px;
  padding: 11px 12px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.mgface-card.dark .mgface-list-item {
  background: rgba(255,255,255,0.07);
  border-color: rgba(24,183,255,0.10);
}

.mgface-list-item strong {
  color: #07111f;
  font-size: 13px;
  font-weight: 950;
}

.mgface-card.dark .mgface-list-item strong {
  color: white;
}

.mgface-list-item span {
  color: #66778e;
  font-size: 12px;
  line-height: 1.45;
}

.mgface-card.dark .mgface-list-item span {
  color: #a9bdd3;
}

.mgface-error {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  color: #991b1b;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.18);
  font-size: 13px;
  line-height: 1.45;
}

.mgface-hidden-canvas {
  display: none;
}

@keyframes mgfaceOrbPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes mgfaceScan {
  0% { top: 14%; opacity: 0; }
  15% { opacity: 1; }
  50% { top: 82%; opacity: 1; }
  100% { top: 14%; opacity: 0; }
}

@media (max-width: 760px) {
  .mgface-card {
    padding: 14px;
    border-radius: 18px;
  }

  .mgface-head,
  .mgface-status {
    display: grid;
    grid-template-columns: 1fr;
  }

  .mgface-chip {
    width: fit-content;
  }

  .mgface-stage {
    min-height: 300px;
  }

  .mgface-grid,
  .mgface-preview-grid {
    grid-template-columns: 1fr;
  }

  .mgface-preview-img {
    width: 100%;
    height: 240px;
  }

  .mgface-actions {
    flex-direction: column;
  }

  .mgface-btn {
    width: 100%;
  }
}
`;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function nowIso() {
  return new Date().toISOString();
}

function getCameraErrorMessage(error) {
  const name = error?.name || "";

  if (name === "NotAllowedError") {
    return "Camera permission was blocked. Allow camera access from browser settings and try again.";
  }

  if (name === "NotFoundError") {
    return "No camera device found on this system.";
  }

  if (name === "NotReadableError") {
    return "Camera is already being used by another app.";
  }

  return error?.message || "Could not start camera.";
}

function estimateQuality({ width, height, brightness, hasImage }) {
  if (!hasImage) return 0;

  let score = 58;

  if (width >= 640 && height >= 420) score += 12;
  if (width >= 960 && height >= 540) score += 8;

  if (brightness >= 65 && brightness <= 205) score += 14;
  if (brightness < 40 || brightness > 225) score -= 18;

  score += 8;

  return clamp(Math.round(score), 0, 100);
}

function createEyeProfile({ width, height, brightness }) {
  const faceBox = {
    x: Math.round(width * 0.28),
    y: Math.round(height * 0.14),
    width: Math.round(width * 0.44),
    height: Math.round(height * 0.66),
  };

  const leftEye = {
    x: Math.round(width * 0.38),
    y: Math.round(height * 0.39),
    width: Math.round(width * 0.10),
    height: Math.round(height * 0.045),
  };

  const rightEye = {
    x: Math.round(width * 0.52),
    y: Math.round(height * 0.39),
    width: Math.round(width * 0.10),
    height: Math.round(height * 0.045),
  };

  const eyeDistancePx = Math.abs(rightEye.x - leftEye.x);
  const faceWidthPx = faceBox.width || 1;

  return {
    schema_version: "local-avatar-face-profile-v1",
    capture_method: "browser_canvas_estimate",
    face_box: faceBox,
    eye_boxes: {
      left_eye: leftEye,
      right_eye: rightEye,
    },
    eye_metrics: {
      estimated_eye_distance_px: eyeDistancePx,
      estimated_face_width_px: faceWidthPx,
      eye_distance_face_ratio: Number((eyeDistancePx / faceWidthPx).toFixed(3)),
      brightness_estimate: brightness,
    },
    avatar_mapping: {
      face_anchor: "front_head_mesh_reference",
      eye_anchor_left: "left_eye_region_reference",
      eye_anchor_right: "right_eye_region_reference",
      texture_mode: "reference_mask_only",
      diagnostic_use: false,
    },
    summary:
      "Estimated face/eye geometry from browser canvas capture. This is an avatar reference layer only, not biometric diagnosis.",
    disclaimer:
      "Face profile is research-only avatar reference data. It is not identity verification, medical diagnosis, or clinical decision support.",
  };
}

function getCanvasBrightness(canvas) {
  try {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;

    const sampleWidth = Math.min(width, 160);
    const sampleHeight = Math.min(height, 120);

    const imageData = ctx.getImageData(
      Math.max(0, Math.floor((width - sampleWidth) / 2)),
      Math.max(0, Math.floor((height - sampleHeight) / 2)),
      sampleWidth,
      sampleHeight
    );

    const data = imageData.data;
    let total = 0;
    let count = 0;

    for (let i = 0; i < data.length; i += 16) {
      const r = data[i] || 0;
      const g = data[i + 1] || 0;
      const b = data[i + 2] || 0;
      total += 0.299 * r + 0.587 * g + 0.114 * b;
      count += 1;
    }

    return count ? Math.round(total / count) : 0;
  } catch {
    return 0;
  }
}

export default function FaceCapturePanel({
  patientId,
  onAvatarFaceProfileReady,
  onSaved,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraOn, setCameraOn] = useState(false);
  const [capturedImage, setCapturedImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [cameraStatus, setCameraStatus] = useState("idle");

  const quality = profile?.capture_quality || 0;

  const statusLabel = useMemo(() => {
    if (saving) return "Saving profile";
    if (profile) return "Face profile attached";
    if (capturedImage) return "Capture ready";
    if (cameraOn) return "Camera scanning";
    return "Camera idle";
  }, [saving, profile, capturedImage, cameraOn]);

  const statusHelper = useMemo(() => {
    if (profile) {
      return "Avatar face/eye geometry is ready and passed to the parent ResearchPage.";
    }

    if (capturedImage) {
      return "Review the capture. You can attach locally or save to backend if endpoint exists.";
    }

    if (cameraOn) {
      return "Align face inside the frame and capture when lighting is stable.";
    }

    return "Start camera to create an avatar reference mask for digital simulation.";
  }, [profile, capturedImage, cameraOn]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
    
  }, []);

  const startCamera = async () => {
    setError("");
    setCameraStatus("starting");

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera API is not available in this browser.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 960 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setCameraOn(true);
      setCameraStatus("active");
    } catch (err) {
      console.error("Camera start failed", err);
      setError(getCameraErrorMessage(err));
      setCameraOn(false);
      setCameraStatus("failed");
    }
  };

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    } catch {
      // no-op
    }

    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraOn(false);
    setCameraStatus("idle");
  };

  const captureFrame = () => {
    setError("");

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      setError("Camera preview is not ready yet.");
      return;
    }

    const width = video.videoWidth || 960;
    const height = video.videoHeight || 720;

    if (!width || !height) {
      setError("Camera frame is not available yet. Wait one second and retry.");
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");

    ctx.save();
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, width, height);
    ctx.restore();

    const brightness = getCanvasBrightness(canvas);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.88);

    const captureQuality = estimateQuality({
      width,
      height,
      brightness,
      hasImage: true,
    });

    const eyeProfile = createEyeProfile({
      width,
      height,
      brightness,
    });

    const nextProfile = {
      patient_id: patientId ? Number(patientId) : null,
      image_data_url: dataUrl,
      descriptor_preview: {
        frame_width: width,
        frame_height: height,
        brightness,
        quality: captureQuality,
        generated_from: "browser_canvas",
      },
      capture_quality: captureQuality,
      eye_profile: eyeProfile,
      eye_analysis: {
        status: "estimated",
        message:
          "Eye geometry is estimated from the captured frame for avatar reference only.",
      },
      created_at: nowIso(),
      source: "digital_face_scanner",
      research_only: true,
      diagnostic_use: false,
      hra_model_note:
        "Current HRA model has anatomical skin mesh without true face UV texture mapping. This capture is stored as avatar mask/reference layer, not a raw square sticker.",
    };

    setCapturedImage(dataUrl);
    setProfile(nextProfile);
    onAvatarFaceProfileReady?.(nextProfile);
  };

  const clearCapture = () => {
    setCapturedImage("");
    setProfile(null);
    setError("");
    onAvatarFaceProfileReady?.(null);
  };

  const saveProfile = async () => {
    if (!profile) {
      setError("Capture face profile first.");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      patient_id: patientId ? Number(patientId) : null,
      capture_quality: profile.capture_quality,
      descriptor_preview: profile.descriptor_preview,
      eye_profile: profile.eye_profile,
      eye_analysis: profile.eye_analysis,
      source: profile.source,
      research_only: true,
      diagnostic_use: false,
      hra_model_note: profile.hra_model_note,
      image_data_url: profile.image_data_url,
      created_at: profile.created_at,
    };

    const possibleEndpoints = [
      "/api/research/avatar-face-profile",
      "/api/research/avatar/face-profile",
      "/api/patient/avatar-face-profile",
      "/api/avatar/face-profile",
    ];

    let savedPayload = null;
    let lastError = null;

    for (const endpoint of possibleEndpoints) {
      try {
        const res = await api.post(endpoint, payload);
        savedPayload = res.data || payload;
        break;
      } catch (err) {
        lastError = err;
      }
    }

    try {
      if (savedPayload) {
        const merged = {
          ...profile,
          saved: true,
          saved_payload: savedPayload,
        };

        setProfile(merged);
        onAvatarFaceProfileReady?.(merged);
        onSaved?.(savedPayload);
      } else {
        const localOnly = {
          ...profile,
          saved: false,
          save_warning:
            lastError?.response?.data?.detail ||
            "Backend save endpoint is not available yet. Profile is attached locally for this simulation.",
        };

        setProfile(localOnly);
        onAvatarFaceProfileReady?.(localOnly);
        onSaved?.(localOnly);

        setError(
          "Backend save endpoint is not available yet. Face profile is attached locally for this simulation."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mgface-panel">
      <style>{facePanelCss}</style>

      <div className="mgface-card dark">
        <div className="mgface-head">
          <div>
            <span className="mgface-kicker">Digital Avatar Scanner</span>
            <h3 className="mgface-title">Face Capture Reference</h3>
            <p className="mgface-subtitle">
              Capture a face reference for avatar identity, face-mask alignment,
              and eye geometry mapping. Research-only, not diagnosis or identity verification.
            </p>
          </div>

          <span className="mgface-chip">
            {patientId ? `Patient #${patientId}` : "No patient"}
          </span>
        </div>

        <div className="mgface-stage">
          {!cameraOn && !capturedImage && (
            <div className="mgface-stage-placeholder">
              <div>
                <div className="mgface-orb" />
                <strong>Avatar face scanner idle</strong>
                <span>
                  Start camera, align the face inside the frame, then capture a
                  reference mask for the 3D avatar workflow.
                </span>
              </div>
            </div>
          )}

          <video
            ref={videoRef}
            className="mgface-video"
            playsInline
            muted
            autoPlay
            style={{ display: cameraOn && !capturedImage ? "block" : "none" }}
          />

          {capturedImage && (
            <img
              className="mgface-canvas-preview"
              src={capturedImage}
              alt="Captured avatar face reference"
            />
          )}

          <div className="mgface-overlay">
            <div className="mgface-scan-line" />
            <div className="mgface-frame" />
            <div className="mgface-corner tl" />
            <div className="mgface-corner tr" />
            <div className="mgface-corner bl" />
            <div className="mgface-corner br" />
          </div>
        </div>

        <canvas ref={canvasRef} className="mgface-hidden-canvas" />

        <div className="mgface-actions">
          {!cameraOn ? (
            <button type="button" className="mgface-btn primary" onClick={startCamera}>
              Start Camera
            </button>
          ) : (
            <button type="button" className="mgface-btn soft" onClick={stopCamera}>
              Stop Camera
            </button>
          )}

          <button
            type="button"
            className="mgface-btn primary"
            onClick={captureFrame}
            disabled={!cameraOn}
          >
            Capture Face
          </button>

          <button
            type="button"
            className="mgface-btn soft"
            onClick={saveProfile}
            disabled={!profile || saving}
          >
            {saving ? "Saving..." : "Save / Attach"}
          </button>

          <button
            type="button"
            className="mgface-btn danger"
            onClick={clearCapture}
            disabled={!capturedImage && !profile}
          >
            Clear
          </button>
        </div>

        <div className="mgface-status">
          <div>
            <strong>{statusLabel}</strong>
            <span>{statusHelper}</span>
          </div>

          <div
            className="mgface-quality"
            style={{ "--quality-value": `${quality}%` }}
          >
            <b>{quality}</b>
            <small>quality</small>
          </div>
        </div>

        {error && <div className="mgface-error">{error}</div>}

        <div className="mgface-grid">
          <div className="mgface-metric">
            <span>Camera</span>
            <strong>{cameraStatus}</strong>
            <small>Browser camera state</small>
          </div>

          <div className="mgface-metric">
            <span>Capture</span>
            <strong>{capturedImage ? "Available" : "Pending"}</strong>
            <small>Image reference status</small>
          </div>

          <div className="mgface-metric">
            <span>Eye profile</span>
            <strong>{profile?.eye_profile ? "Estimated" : "Pending"}</strong>
            <small>Avatar eye anchor mapping</small>
          </div>

          <div className="mgface-metric">
            <span>Mode</span>
            <strong>Research only</strong>
            <small>Not clinical diagnosis</small>
          </div>
        </div>
      </div>

      {profile && (
        <div className="mgface-card">
          <div className="mgface-head">
            <div>
              <span className="mgface-kicker">Avatar Reference</span>
              <h3 className="mgface-title">Captured face profile</h3>
              <p className="mgface-subtitle">
                This compact profile is sent back to ResearchPage through
                <b> onAvatarFaceProfileReady</b> and added to simulation payload.
              </p>
            </div>

            <span className="mgface-chip">
              {profile.saved ? "Saved" : "Local"}
            </span>
          </div>

          <div className="mgface-preview-grid">
            <img
              className="mgface-preview-img"
              src={capturedImage}
              alt="Avatar face profile preview"
            />

            <div className="mgface-list">
              <div className="mgface-list-item">
                <strong>Frame</strong>
                <span>
                  {profile.descriptor_preview?.frame_width || "—"} ×{" "}
                  {profile.descriptor_preview?.frame_height || "—"} · brightness{" "}
                  {profile.descriptor_preview?.brightness ?? "—"}
                </span>
              </div>

              <div className="mgface-list-item">
                <strong>Eye distance ratio</strong>
                <span>
                  {profile.eye_profile?.eye_metrics?.eye_distance_face_ratio ?? "—"}{" "}
                  estimated from canvas reference.
                </span>
              </div>

              <div className="mgface-list-item">
                <strong>Avatar mapping</strong>
                <span>
                  {profile.eye_profile?.avatar_mapping?.texture_mode ||
                    "reference_mask_only"}
                </span>
              </div>
            </div>
          </div>

          <div className="mgface-section">
            <h4>How this is used</h4>
            <p>
              The capture is attached to the digital avatar simulation as a
              face-mask reference layer. It helps keep avatar context consistent
              while testing compounds, medicines, proteins, organ impact, and
              research hypotheses.
            </p>
          </div>

          <div className="mgface-section">
            <h4>Safety note</h4>
            <p>
              This is not face recognition, not diagnosis, not biometric identity
              verification, and not clinical decision support. It is only a
              research-avatar reference for the current simulation workflow.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}